import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllVip, getAllGuard } from "../api/vipform";
import { useVipStore } from "../context/VipContext";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { getToken } from "firebase/messaging";
import { messaging } from "../api/firebase";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function VipDashboard() {
  const navigate = useNavigate();
  const { selectedVip, setSelectedVip } = useVipStore();

  const [vipName, setVipName] = useState("VIP");
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [totalNotification, setTotalNotification] = useState(0);
  const [totalActiveGuards, setTotalActiveGuards] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showNotifyPopup, setShowNotifyPopup] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (selectedVip?.name) {
      setVipName(selectedVip.name);
      loadDashboard(selectedVip.id);
    }
  }, [selectedVip]);

  useEffect(() => {
    const syncVipProfile = async () => {
      try {
        if (selectedVip?.name) return;

        const stored = localStorage.getItem("selectedVip");
        if (stored) {
          const parsed = JSON.parse(stored);
          setSelectedVip(parsed);
          setVipName(parsed.name);
          fetchTotalAssignments(parsed.id);
          fetchTotalNotifications(parsed.id);
          fetchTotalActiveGuards(parsed.id);
          return;
        }

        const token = localStorage.getItem("vipToken");
        if (!token) return;

        const decoded = jwtDecode(token);
        const username = decoded.username || decoded.sub || decoded.email;

        const res = await axios.get(`${BASE_URL}/api/categories/profile`, {
          params: { username },
          headers: { Authorization: `Bearer ${token}` },
        });

        const profile = Array.isArray(res.data) ? res.data[0] : res.data;

        setSelectedVip(profile);
        setVipName(profile.name);
        localStorage.setItem("selectedVip", JSON.stringify(profile));

        fetchTotalAssignments(profile.id);
        fetchTotalNotifications(profile.id);
        fetchTotalActiveGuards(profile.id);
      } catch (err) {
        console.log("VIP Sync Error:", err);
      }
    };

    syncVipProfile();
  }, []);

  const loadDashboard = async (vipId) => {
    setLoading(true);
    await Promise.all([
      fetchTotalAssignments(vipId),
      fetchTotalNotifications(vipId),
      fetchTotalActiveGuards(vipId),
    ]);
    setLoading(false);
  };

  const fetchTotalAssignments = async (vipId) => {
  try {
    const token =
      localStorage.getItem("vipToken") || localStorage.getItem("token");

    const res = await axios.get(
      `${BASE_URL}/api/assignments/vip/${vipId}/history`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = res.data;

    // ✅ FORCE ARRAY (same as VipDutyHistory)
    const history = Array.isArray(data)
      ? data
      : data && typeof data === "object"
      ? [data]
      : [];

    // ✅ COUNT OFFICERS ACROSS ALL DUTIES
    const totalOfficers = history.reduce((sum, duty) => {
      const officers = Array.isArray(duty.officers)
        ? duty.officers
        : duty.officers
        ? [duty.officers]
        : [];

      return sum + officers.length;
    }, 0);

    setTotalAssignments(totalOfficers);
  } catch (err) {
    console.error("Total Officers Assigned Error:", err);
    setTotalAssignments(0);
  }
};

  const fetchTotalNotifications = async (vipId) => {
    try {
      const token = localStorage.getItem("vipToken");
      const res = await axios.get(`${BASE_URL}/api/notification/${vipId}/vip`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const list = Array.isArray(res.data) ? res.data : [];
      setTotalNotification(list.length);
    } catch (err) {
      console.error("Total notification fetch error:", err);
      setTotalNotification(0);
    }
  };

  const fetchTotalActiveGuards = async (vipId) => {
    try {
      const token = localStorage.getItem("vipToken");
      const res = await axios.get(`${BASE_URL}/api/assignments/${vipId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const officers = Array.isArray(res.data?.officer) ? res.data.officer : [];
      setTotalActiveGuards(officers.length);
    } catch (err) {
      console.error("Active guards fetch error:", err);
      setTotalActiveGuards(0);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const stats = [
    {
      title: "Total Officers Assigned",
      value: totalAssignments,
      icon: "fa-file-alt",
      color: "#a855f7",
    },
    {
      title: "Total Notifications",
      value: totalNotification,
      icon: "fa-bell",
      color: "#10b981",
    },
    {
      title: "Active Guards",
      value: totalActiveGuards,
      icon: "fa-shield-alt",
      color: "#a855f7",
    },
  ];

  const handleNotificationPermission = async () => {
    try {
      setNotifyLoading(true);
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setNotifyMessage("Notification permission not accepted.");
        setNotifyLoading(false);
        return;
      }

      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });

      if (token) {
        await axios.post(
          `${BASE_URL}/api/notification/subscribe`,
          {
            notificationToken: token,
            notificationSenderId: selectedVip?.id,
            notificationSenderRole: "vip",
            notificationSenderName: vipName,
            notificationMessage: `${vipName} subscribed Successfully`,
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("vipToken")}` },
          }
        );
      }

      setTimeout(() => {
        setShowNotifyPopup(false);
        setNotifyLoading(false);
      }, 3000);
    } catch (err) {
      console.error(err);
      setNotifyLoading(false);
    }
  };

  useEffect(() => {
    if (Notification.permission === "granted") {
      setShowNotifyPopup(false);
    } else {
      setShowNotifyPopup(true);
      setNotifyMessage("Enable notifications to stay updated on your security.");
    }
  }, []);

  const isMobile = windowWidth <= 991;

  return (
    <>
      <div className="vip-dashboard-wrapper">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-title">
              <h1>VIP Dashboard</h1>
              <p>Monitor your security assignments and protection status</p>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <i className="fas fa-sign-out-alt"></i>
              <span className="btn-text">Logout</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="dashboard-main">
          {/* Welcome */}
          <section className="welcome-card">
            <h2>Welcome back, {vipName} 👋</h2>
            <p className="welcome-subtitle">Here's your security overview at a glance.</p>
          </section>

          {/* Loading */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading your dashboard data...</p>
            </div>
          ) : (
            <>
              {/* Stats */}
              <section className="stats-grid">
                {stats.map((stat, i) => (
                  <div key={i} className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: stat.color }}>
                      <i className={`fas ${stat.icon}`}></i>
                    </div>
                    <div className="stat-info">
                      <h3 className="stat-value">{stat.value}</h3>
                      <p className="stat-title">{stat.title}</p>
                    </div>
                  </div>
                ))}
              </section>

              {/* Activity */}
              <section className="activity-card">
                <h3>Recent Activity</h3>
                <ul className="activity-list">
                  <li><i className="fas fa-check"></i> Security assignments synchronized</li>
                  <li><i className="fas fa-check"></i> Dashboard loaded successfully</li>
                  <li><i className="fas fa-check"></i> All protection systems active</li>
                  <li><i className="fas fa-bell"></i> Real-time alerts enabled</li>
                </ul>
              </section>
            </>
          )}
        </main>

        {/* Notification Toast */}
        {showNotifyPopup && !isMobile && (
          <div className="notification-toast">
            <div className="toast-header">
              <i className="fas fa-bell"></i>
              <strong>Enable Notifications</strong>
            </div>
            <p>{notifyMessage}</p>
            <button
              onClick={handleNotificationPermission}
              disabled={notifyLoading}
              className="toast-btn"
            >
              {notifyLoading ? "Processing..." : "Allow Notifications"}
            </button>
          </div>
        )}
      </div>

      {/* Light Purple Glassmorphic Theme */}
      <style jsx>{`
        .vip-dashboard-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0d4ff 0%, #f0e6ff 100%);
          color: #4c1d95;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }

        .dashboard-header {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(168, 85, 247, 0.4);
          padding: 1.2rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
          width: 100%;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #9333ea;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.9rem;
          color: #6b46c1;
        }

        .logout-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .logout-btn:hover {
          background: #dc2626;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
        }

        .btn-text { display: inline; }

        .dashboard-main {
          padding: 1.5rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }

        @media (max-width: 768px) {
          .dashboard-main { padding: 1rem; }
          .dashboard-header { padding: 1rem 1.5rem; }
        }

        .welcome-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
        }

        .welcome-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(168, 85, 247, 0.25);
        }

        .welcome-card h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #6b21a8;
        }

        .welcome-subtitle {
          opacity: 0.85;
          font-size: 1rem;
          color: #7c3aed;
        }

        .loading-container {
          text-align: center;
          padding: 3rem;
          color: #9333ea;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e9d5ff;
          border-top: 4px solid #a855f7;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 15px 35px rgba(168, 85, 247, 0.3);
        }

        .stat-icon {
          width: 70px;
          height: 70px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.8rem;
          flex-shrink: 0;
        }

        .stat-info {
          flex-grow: 1;
        }

        .stat-value {
          font-size: 2.4rem;
          font-weight: 800;
          margin: 0;
          color: #581c87;
        }

        .stat-title {
          opacity: 0.9;
          margin-top: 0.4rem;
          font-size: 1rem;
          font-weight: 500;
          color: #7c3aed;
        }

        .activity-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
        }

        .activity-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 35px rgba(168, 85, 247, 0.25);
        }

        .activity-card h3 {
          margin-bottom: 1.4rem;
          font-weight: 600;
          color: #9333ea;
          font-size: 1.3rem;
        }

        .activity-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .activity-list li {
          padding: 0.8rem 0;
          border-bottom: 1px solid rgba(168, 85, 247, 0.3);
          display: flex;
          align-items: center;
          gap: 0.8rem;
          font-size: 1rem;
          color: #5b21b6;
        }

        .activity-list li:last-child {
          border-bottom: none;
        }

        .activity-list i {
          color: #10b981;
          font-size: 1rem;
        }

        .notification-toast {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 340px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border-radius: 14px;
          padding: 1.4rem;
          box-shadow: 0 15px 40px rgba(168, 85, 247, 0.3);
          border: 1px solid #a855f7;
          z-index: 9999;
          animation: slideIn 0.5s ease;
          color: #4c1d95;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .toast-header {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 0.8rem;
          color: #9333ea;
          font-weight: 600;
        }

        .toast-btn {
          width: 100%;
          padding: 0.8rem;
          background: #a855f7;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          margin-top: 1rem;
          transition: all 0.3s;
        }

        .toast-btn:hover {
          background: #9333ea;
        }

        .toast-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
}


// //---------------------3--------------------------
// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { getAllVip, getAllGuard } from "../api/vipform";
// import { useVipStore } from "../context/VipContext";
// import axios from "axios";
// import { jwtDecode } from "jwt-decode";

// // ✅ ONLY ADDITION
// import { getToken } from "firebase/messaging";
// import { messaging } from "../api/firebase";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function VipDashboard() {
//   const navigate = useNavigate();
//   const { selectedVip, setSelectedVip } = useVipStore();

//   const [vipName, setVipName] = useState("VIP");
//   const [totalAssignments, setTotalAssignments] = useState(0);
//   const [totalNotification, setTotalNotification] = useState(0);
//   const [totalActiveGuards, setTotalActiveGuards] = useState(0);
//   const [vipList, setVipList] = useState([]);
//   const [guardList, setGuardList] = useState([]);
//   const [loading, setLoading] = useState(true);

//   // 🔔 notification popup (ONLY ADDITION)
//   const [showNotifyPopup, setShowNotifyPopup] = useState(false);
//   const [notifyMessage, setNotifyMessage] = useState("");
//   const [notifyLoading, setNotifyLoading] = useState(false);

//   useEffect(() => {
//     if (selectedVip?.name) {
//       setVipName(selectedVip.name);
//       loadDashboard(selectedVip.id);
//     }
//   }, [selectedVip]);

//   useEffect(() => {
//     const syncVipProfile = async () => {
//       try {
//         if (selectedVip?.name) return;

//         const stored = localStorage.getItem("selectedVip");
//         if (stored) {
//           const parsed = JSON.parse(stored);
//           setSelectedVip(parsed);
//           setVipName(parsed.name);
//           fetchTotalAssignments(parsed.id);
//           fetchTotalNotifications(parsed.id);
//           fetchTotalActiveGuards(parsed.id);
//           return;
//         }

//         const token = localStorage.getItem("vipToken");
//         if (!token) return;

//         const decoded = jwtDecode(token);
//         const username = decoded.username || decoded.sub || decoded.email;

//         const res = await axios.get(`${BASE_URL}/api/categories/profile`, {
//           params: { username },
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         const profile = Array.isArray(res.data) ? res.data[0] : res.data;

//         setSelectedVip(profile);
//         setVipName(profile.name);
//         localStorage.setItem("selectedVip", JSON.stringify(profile));

//         fetchTotalAssignments(profile.id);
//         fetchTotalNotifications(profile.id);
//         fetchTotalActiveGuards(profile.id);
//       } catch (err) {
//         console.log("VIP Sync Error:", err);
//       }
//     };

//     syncVipProfile();
//   }, []);

//   const loadDashboard = async (vipId) => {
//     setLoading(true);
//     await fetchTotalAssignments(vipId);
//     await fetchTotalNotifications(vipId);
//     await fetchTotalActiveGuards(vipId);
//     setLoading(false); // ✅ THIS WAS MISSING
//   };
//   const fetchTotalAssignments = async (vipId) => {
//     try {
//       const token =
//         localStorage.getItem("vipToken") || localStorage.getItem("token");

//       const res = await axios.get(
//         `${BASE_URL}/api/assignments/vip/${vipId}/history`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       const data = res.data;

//       // 🔥 FORCE ARRAY
//       const history = Array.isArray(data)
//         ? data
//         : data && typeof data === "object"
//         ? [data]
//         : [];

//       // ✅ COUNT OFFICERS, NOT DUTIES
//       const totalOfficers = history.reduce((sum, duty) => {
//         if (Array.isArray(duty.officer)) {
//           return sum + duty.officer.length;
//         }
//         if (duty.officer) {
//           return sum + 1;
//         }
//         return sum;
//       }, 0);

//       setTotalAssignments(totalOfficers);
//     } catch (err) {
//       console.error("Total Assignments Error:", err);
//       setTotalAssignments(0);
//     }
//   };

//  const fetchTotalNotifications = async (vipId) => {
//   try {
//     const token = localStorage.getItem("vipToken");

//     const res = await axios.get(
//       `${BASE_URL}/api/notification/${vipId}/vip`,
//       {
//         headers: { Authorization: `Bearer ${token}` },
//       }
//     );

//     const list = Array.isArray(res.data) ? res.data : [];
//     setTotalNotification(list.length);
//   } catch (err) {
//     console.error("Total notification fetch error:", err);
//     setTotalNotification(0);
//   }
// };

//   const fetchTotalActiveGuards = async (vipId) => {
//     try {
//       const token = localStorage.getItem("vipToken");

//       const res = await axios.get(`${BASE_URL}/api/assignments/${vipId}`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       const officers = Array.isArray(res.data?.officer) ? res.data.officer : [];

//       // ✅ THIS API ONLY RETURNS ACTIVE DUTY
//       setTotalActiveGuards(officers.length);
//     } catch (err) {
//       console.error("Active guards fetch error:", err);
//       setTotalActiveGuards(0);
//     }
//   };

//   const loadVip = async () => {
//     const data = await getAllVip();
//     setVipList(Array.isArray(data) ? data : data?.data || []);
//   };

//   const loadGuards = async () => {
//     try {
//       let allGuards = [];
//       let page = 0;
//       let totalPages = 1;

//       while (page < totalPages) {
//         const res = await getAllGuard(page, 100);
//         allGuards = [...allGuards, ...(res.content || [])];
//         totalPages = res.totalPages;
//         page++;
//       }

//       setGuardList(allGuards);
//     } catch (err) {
//       console.log("Guard Load Error:", err);
//     }
//   };

//   useEffect(() => {
//     const loadAll = async () => {
//       setLoading(true);
//       await loadVip();
//       await loadGuards();
//       setLoading(false);
//     };

//     loadAll();
//   }, []);

//   const handleLogout = () => {
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   const totalUsers = vipList.length;
//   const stats = [
//     {
//       title: "Total Officer Assigned",
//       value: totalAssignments,
//       icon: "fas fa-file",
//       color: "#1e73be",
//     },

//     {
//       title: "Total Notifications",
//       value: totalNotification,
//       icon: "fas fa-bell",
//       color: "#3cb371",
//     },

//     {
//       title: "Total Active Guards",
//       value: totalActiveGuards,
//       icon: "fas fa-shield-alt",
//       color: "#6d5cf0ff",
//     },
//     // { title: "Total Vips Registered", value: totalUsers, icon: "fas fa-users", color: "#ffa500" },
//   ];

//   /* -----------------------------------
//       🔔 NOTIFICATION (ONLY ADDITION)
//   ------------------------------------ */
//   const handleNotificationPermission = async () => {
//     try {
//       setNotifyLoading(true);

//       const permission = await Notification.requestPermission();
//       if (permission !== "granted") {
//         setNotifyMessage("Notification permission not accepted.");
//         setNotifyLoading(false);
//         return;
//       }

//       const token = await getToken(messaging, {
//         vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
//       });

//       if (token) {
//         await axios.post(
//           `${BASE_URL}/api/notification/subscribe`,
//           {
//             notificationToken: token,
//             notificationSenderId: selectedVip?.id,
//             notificationSenderRole: "vip",
//             notificationSenderName: vipName,
//             notificationMessage: `${vipName} subscribed Successfully`,
//           },
//           {
//             headers: {
//               Authorization: `Bearer ${localStorage.getItem("vipToken")}`,
//             },
//           }
//         );
//       }

//       setTimeout(() => {
//         setShowNotifyPopup(false);
//         setNotifyLoading(false);
//       }, 5000);
//     } catch (err) {
//       console.error(err);
//       setNotifyLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (Notification.permission === "granted") {
//       setShowNotifyPopup(false);
//     } else {
//       setShowNotifyPopup(true);
//       setNotifyMessage("Please allow notification permission.");
//     }
//   }, []);

//   return (
//     <div style={styles.page}>
//       <div style={styles.headerSection}>
//         <div>
//           <h2 style={styles.pageTitle}>VIP DASHBOARD</h2>
//           <p style={styles.desc}>Manage VIPs, Guards & Combined User Count.</p>
//         </div>

//         <button style={styles.logoutBtn} onClick={handleLogout}>
//           <i className="fas fa-sign-out-alt" style={{ marginRight: 8 }}></i>{" "}
//           Logout
//         </button>
//       </div>

//       <div style={styles.mainContent}>
//         <div style={styles.welcomeBox}>
//           <h2 style={styles.welcomeText}>Welcome {vipName} 👋</h2>
//         </div>

//         {loading ? (
//           <h3 style={{ textAlign: "center", marginTop: 40 }}>
//             Loading Dashboard...
//           </h3>
//         ) : (
//           <>
//             <div style={styles.statsGrid}>
//               {stats.map((stat, index) => (
//                 <div
//                   key={index}
//                   style={styles.statCard}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.transform = "scale(1.05)";
//                     e.currentTarget.style.boxShadow =
//                       "0 10px 25px rgba(0,0,0,0.15)";
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.transform = "scale(1)";
//                     e.currentTarget.style.boxShadow =
//                       "0 5px 20px rgba(0,0,0,0.08)";
//                   }}
//                 >
//                   <div
//                     style={{
//                       ...styles.iconCircle,
//                       background: stat.color,
//                     }}
//                   >
//                     <i className={stat.icon} style={styles.icon}></i>
//                   </div>
//                   <h3 style={styles.statValue}>{stat.value}</h3>
//                   <p style={styles.statTitle}>{stat.title}</p>
//                 </div>
//               ))}
//             </div>

//             <div style={styles.activityBox}>
//               <h3 style={styles.activityTitle}>Recent Activity</h3>
//               <ul style={styles.activityList}>
//                 <li>✔ VIP & Guards list updated</li>
//                 <li>✔ Dashboard loaded successfully</li>
//                 <li>✔ System running smoothly</li>
//               </ul>
//             </div>
//           </>
//         )}
//       </div>

//       {showNotifyPopup && (
//         <div
//           style={{
//             position: "fixed",
//             bottom: 20,
//             right: 20,
//             width: 280,
//             background: "#fff",
//             padding: 14,
//             borderRadius: 8,
//             boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
//           }}
//         >
//           <h6>🔔 Notification Required</h6>
//           <p>{notifyMessage}</p>
//           <button
//             className="btn btn-primary w-100"
//             disabled={notifyLoading}
//             onClick={handleNotificationPermission}
//           >
//             {notifyLoading ? "Please wait..." : "Allow Notifications"}
//           </button>
//         </div>
//       )}
//     </div>
//   );
// }

// const styles = {
//   page: { padding: 25 },
//   headerSection: {
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     marginBottom: 20,
//   },
//   pageTitle: { fontSize: 30, fontWeight: 700, color: "#1e73be" },
//   desc: { fontSize: 15, opacity: 0.6 },
//   logoutBtn: {
//     background: "#888",
//     padding: "10px 20px",
//     borderRadius: 8,
//     color: "#fff",
//     border: "none",
//     cursor: "pointer",
//     fontWeight: 600,
//     display: "flex",
//     alignItems: "center",
//   },
//   mainContent: {
//     background: "#fff",
//     padding: 25,
//     borderRadius: 12,
//     boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
//   },
//   welcomeBox: { marginBottom: 25 },
//   welcomeText: {
//     fontSize: 26,
//     fontWeight: 700,
//     color: "#1e73be",
//     marginBottom: 20,
//   },
//   statsGrid: {
//     display: "grid",
//     gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
//     gap: 25,
//     marginTop: 20,
//   },
//   statCard: {
//     background: "white",
//     borderRadius: 12,
//     padding: 25,
//     textAlign: "center",
//     boxShadow: "0 5px 20px rgba(0,0,0,0.08)",
//     transition: "all 0.3s ease", //  hover animation enabled
//     cursor: "pointer",
//   },
//   iconCircle: {
//     width: 55,
//     height: 55,
//     borderRadius: "50%",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     margin: "0 auto 15px",
//   },
//   icon: { fontSize: 22, color: "white" },
//   statValue: { fontSize: 28, fontWeight: 700, color: "#333" },
//   statTitle: { opacity: 0.6 },
//   activityBox: {
//     marginTop: "17%",
//     padding: 20,
//     borderRadius: 12,
//     background: "#f8fbff",
//     border: "1px solid #e5e9f0",
//   },
//   activityTitle: {
//     fontSize: 20,
//     color: "#1e73be",
//     marginBottom: 10,
//   },
//   activityList: { lineHeight: 2, paddingLeft: 10 },
// };
